import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { ShieldCheck, Plus, Pencil, UserX, Settings2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const Sel = (p) => <select {...p} className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />;
const ROLE_STYLE = { owner: "bg-amber-100 text-amber-700 border-amber-200", admin: "bg-indigo-100 text-indigo-700 border-indigo-200", accountant: "bg-blue-100 text-blue-700 border-blue-200", staff: "bg-slate-100 text-slate-600 border-slate-200" };
const PRESET_ROLES = ["admin", "accountant", "staff"];
const prettyPerm = (p) => p.replace(".", " · ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function Users() {
  const { user: me, can } = useAuth();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ modules: [], all_perms: [], role_templates: {} });
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});
  const [del, setDel] = useState(null);
  const [roleMgr, setRoleMgr] = useState(false);
  const [roles, setRoles] = useState([]);
  const [roleForm, setRoleForm] = useState(null);
  const canEdit = can("users.edit");

  const load = () => { api.get("/users").then(setItems).catch(() => {}); api.get("/rbac/meta").then(setMeta).catch(() => {}); api.get("/roles").then(setRoles).catch(() => {}); };
  useEffect(() => { load(); }, []);

  const roleOptions = meta.roles || PRESET_ROLES;
  const openNew = () => { setEdit(null); setForm({ role: "staff", permissions: meta.role_templates?.staff || [], active: true, password: "" }); setOpen(true); };
  const openEdit = (u) => { setEdit(u); setForm({ ...u, permissions: u.permissions || meta.role_templates?.[u.role] || [], password: "" }); setOpen(true); };
  const applyRoleTemplate = (role) => setForm((f) => ({ ...f, role, permissions: meta.role_templates?.[role] || [] }));
  const togglePerm = (p) => setForm((f) => ({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p] }));

  const openRoleNew = () => setRoleForm({ name: "", permissions: [] });
  const openRoleEdit = (r) => setRoleForm({ ...r });
  const toggleRolePerm = (p) => setRoleForm((f) => ({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p] }));
  const saveRole = async () => {
    if (!roleForm.name.trim()) return toast.error("Role name is required");
    try {
      if (roleForm.id) await api.put(`/roles/${roleForm.id}`, { name: roleForm.name, permissions: roleForm.permissions });
      else await api.post("/roles", { name: roleForm.name, permissions: roleForm.permissions });
      toast.success("Role saved"); setRoleForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const deleteRole = async (r) => {
    try { await api.del(`/roles/${r.id}`); toast.success("Role deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const save = async () => {
    if (!form.email && !edit) return toast.error("Email is required");
    if (!edit && !form.password) return toast.error("Password is required");
    try {
      if (edit) await api.put(`/users/${edit.id}`, { name: form.name, role: form.role, permissions: form.permissions, active: form.active, ...(form.password ? { password: form.password } : {}) });
      else await api.post("/users", form);
      toast.success("User saved"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const removeUser = async () => { try { await api.del(`/users/${del.id}`); toast.success(`${del.name || "User"} removed from this business`); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } setDel(null); load(); };

  return (
    <div>
      <PageHeader title="Users & Roles" subtitle="Manage team members, roles and per-module permissions" testid="users-header">
        {canEdit && <Button variant="outline" onClick={() => setRoleMgr(true)} data-testid="manage-roles-btn"><Settings2 className="w-4 h-4 mr-1" /> Manage Roles</Button>}
        {canEdit && <Button data-testid="add-user-btn" onClick={openNew} className="bg-indigo-600 hover:bg-indigo-700"><Plus className="w-4 h-4 mr-1" /> Add User</Button>}
      </PageHeader>
      {items.length === 0 ? <EmptyState icon={ShieldCheck} title="No users" hint="Add team members with tailored access." /> : (
        <Card className="border-slate-200 shadow-sm overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Access</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-slate-50/50">
                  <TableCell className="font-medium">{u.name}{u.id === me?.id && <span className="text-xs text-slate-400"> (you)</span>}</TableCell>
                  <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                  <TableCell><Badge variant="outline" className={`capitalize ${ROLE_STYLE[u.role] || ""}`}>{u.role}</Badge></TableCell>
                  <TableCell className="text-xs text-slate-500">{["owner", "admin"].includes(u.role) ? "Full access" : `${(u.permissions || []).length} permissions`}</TableCell>
                  <TableCell>{u.active ? <span className="text-emerald-600 text-sm">Active</span> : <span className="text-rose-600 text-sm">Disabled</span>}</TableCell>
                  <TableCell className="text-right">
                    {canEdit && <Button size="sm" variant="ghost" title="Edit user & permissions" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="w-4 h-4" /></Button>}
                    {can("users.delete") && u.role !== "owner" && u.id !== me?.id && <Button size="sm" variant="ghost" className="text-rose-600" title="Delete user from business" onClick={() => setDel(u)} data-testid={`delete-user-${u.id}`}><UserX className="w-4 h-4" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{edit ? "Edit User" : "Add User"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Name"><Input data-testid="user-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Email">{edit ? <Input value={form.email} disabled /> : <Input data-testid="user-email" type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} />}</Field>
            <Field label="Role"><Sel data-testid="user-role" value={form.role} onChange={(e) => applyRoleTemplate(e.target.value)}>{roleOptions.map((r) => <option key={r} value={r} className="capitalize">{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}</Sel></Field>
            <Field label={edit ? "Reset Password (optional)" : "Password"}><Input data-testid="user-password" type="password" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={edit ? "Leave blank to keep" : "Set a password"} /></Field>
          </div>
          {form.role !== "admin" && (
            <div className="mt-2">
              <p className="text-xs font-semibold uppercase text-slate-500 mb-2">Permissions (per module)</p>
              <div className="border rounded-md divide-y max-h-72 overflow-y-auto">
                {meta.modules.map((m) => (
                  <div key={m} className="flex items-center justify-between px-3 py-2">
                    <span className="text-sm capitalize text-slate-700">{m}</span>
                    <div className="flex gap-4">
                      {["view", "create", "edit", "delete", "cancel"].map((a) => {
                        const p = `${m}.${a}`;
                        return (
                          <label key={p} className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer">
                            <Checkbox checked={form.permissions?.includes(p)} onCheckedChange={() => togglePerm(p)} data-testid={`perm-${p}`} /> {a}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {form.role === "admin" && <p className="text-sm text-indigo-600 bg-indigo-50 rounded-md p-3">Admins have full access to every module including user management.</p>}
          <div className="flex items-center gap-2 mt-2"><Checkbox checked={form.active !== false} onCheckedChange={(v) => setForm({ ...form, active: !!v })} data-testid="user-active" /><span className="text-sm text-slate-600">Account active</span></div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="save-user-btn" onClick={save} className="bg-indigo-600 hover:bg-indigo-700">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={() => setDel(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete this user?</AlertDialogTitle><AlertDialogDescription>{del?.name} ({del?.email}) will be removed from this business and lose all access. You can add them again later.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={removeUser} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-user-btn">Delete user</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>

      <Dialog open={roleMgr} onOpenChange={setRoleMgr}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Custom Roles</DialogTitle></DialogHeader>
          {!roleForm ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">Define reusable roles with a tailored set of module permissions, beyond Admin / Accountant / Staff.</p>
              {roles.length === 0 ? <p className="text-sm text-slate-400 py-4 text-center">No custom roles yet.</p> : (
                <div className="border rounded-md divide-y">
                  {roles.map((r) => (
                    <div key={r.id} className="flex items-center justify-between px-3 py-2" data-testid={`role-row-${r.id}`}>
                      <div><p className="text-sm font-medium capitalize text-slate-700">{r.name}</p><p className="text-xs text-slate-400">{(r.permissions || []).length} permissions</p></div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" title="Edit role" onClick={() => openRoleEdit(r)} data-testid={`edit-role-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" className="text-rose-600" title="Delete role" onClick={() => deleteRole(r)} data-testid={`delete-role-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <Button onClick={openRoleNew} className="bg-indigo-600 hover:bg-indigo-700 w-full" data-testid="new-role-btn"><Plus className="w-4 h-4 mr-1" /> New Custom Role</Button>
            </div>
          ) : (
            <div className="space-y-3">
              <Field label="Role Name"><Input value={roleForm.name} onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })} placeholder="e.g. Property Manager" data-testid="role-name" /></Field>
              <p className="text-xs font-semibold uppercase text-slate-500">Permissions (per module)</p>
              <div className="border rounded-md divide-y max-h-64 overflow-y-auto">
                {meta.modules.map((m) => (
                  <div key={m} className="flex items-center justify-between px-3 py-2">
                    <span className="text-sm capitalize text-slate-700">{m}</span>
                    <div className="flex gap-4">
                      {["view", "create", "edit", "delete", "cancel"].map((a) => { const p = `${m}.${a}`; return (
                        <label key={p} className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer">
                          <Checkbox checked={roleForm.permissions?.includes(p)} onCheckedChange={() => toggleRolePerm(p)} data-testid={`role-perm-${p}`} /> {a}
                        </label>); })}
                    </div>
                  </div>
                ))}
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setRoleForm(null)}>Back</Button><Button onClick={saveRole} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-role-btn">Save Role</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
